import { lazy, Suspense } from 'react'
import type { ComponentType } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { Center, Loader } from '@mantine/core'
import { LandingPage } from './components/landing/LandingPage'
import { UserProvider } from './contexts/UserContext'
import { DoctorRobotinProvider } from './contexts/DoctorRobotinContext'

const RELOADED_KEY = 'reloaded-for-new-version'

/**
 * A page that is downloaded the first time it is opened.
 *
 * The landing page is what most visitors see, so it is the only page in the
 * first download; everything behind the login arrives on demand.
 *
 * Publishing a new version removes the files of the old one. A tab left open
 * from before then fails to fetch a page it had not opened yet: reloading once
 * brings the new version instead of leaving a blank screen.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- the same bound React.lazy puts on a component
function lazyPage<Page extends ComponentType<any>>(load: () => Promise<{ default: Page }>) {
  return lazy<Page>(async () => {
    try {
      const page = await load()
      try { sessionStorage.removeItem(RELOADED_KEY) } catch { /* storage unavailable */ }
      return page
    } catch (error) {
      let alreadyReloaded = true
      try {
        alreadyReloaded = sessionStorage.getItem(RELOADED_KEY) === '1'
        sessionStorage.setItem(RELOADED_KEY, '1')
      } catch { /* storage unavailable: do not risk a reload loop */ }
      if (alreadyReloaded) throw error
      window.location.reload()
      // The reload takes over; keep showing the loader until it does
      return new Promise<never>(() => {})
    }
  })
}

const AuthPage = lazyPage(() => import('./components/auth/AuthPage'))
const Dashboard = lazyPage(() => import('./components/Dashboard'))
const InventoryPage = lazyPage(() => import('./components/InventoryPage').then(m => ({ default: m.InventoryPage })))
const MovementRegistrationPage = lazyPage(() => import('./components/MovementRegistrationPage').then(m => ({ default: m.MovementRegistrationPage })))
const HistoryPage = lazyPage(() => import('./components/HistoryPage').then(m => ({ default: m.HistoryPage })))
const StatsPage = lazyPage(() => import('./components/StatsPage').then(m => ({ default: m.StatsPage })))
const DataManagementPage = lazyPage(() => import('./components/DataManagementPage').then(m => ({ default: m.DataManagementPage })))
const NotesPage = lazyPage(() => import('./components/NotesPage').then(m => ({ default: m.NotesPage })))
const DoctorRobotinPage = lazyPage(() => import('./components/DoctorRobotinPage').then(m => ({ default: m.DoctorRobotinPage })))
const ProfilePage = lazyPage(() => import('./components/ProfilePage').then(m => ({ default: m.ProfilePage })))
const SupportPage = lazyPage(() => import('./components/SupportPage').then(m => ({ default: m.SupportPage })))
const AdminPage = lazyPage(() => import('./components/admin/AdminPage').then(m => ({ default: m.AdminPage })))
const ContenidoPage = lazyPage(() => import('./components/admin/ContenidoPage').then(m => ({ default: m.ContenidoPage })))
const InteresadosPage = lazyPage(() => import('./components/admin/InteresadosPage').then(m => ({ default: m.InteresadosPage })))

const PageLoading = () => (
  <Center mih="100vh">
    <Loader size="lg" />
  </Center>
)

function App() {
  return (
    <UserProvider>
      <DoctorRobotinProvider>
      <Router>
        <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/auth/login" element={<AuthPage />} />
          <Route path="/auth/register" element={<AuthPage />} />
          
          {/* Dashboard routes */}
          <Route path="/dashboard" element={<Dashboard />}>
            <Route index element={<InventoryPage />} />
            <Route path="movement" element={<MovementRegistrationPage />} />
            <Route path="history" element={<HistoryPage />} />
            <Route path="stats" element={<StatsPage />} />
            <Route path="data" element={<DataManagementPage />} />
            {/* Redirects for old routes */}
            <Route path="backups" element={<Navigate to="/dashboard/data" replace />} />
            <Route path="import-export" element={<Navigate to="/dashboard/data" replace />} />
            <Route path="notes" element={<NotesPage />} />
            <Route path="doctor" element={<DoctorRobotinPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="support" element={<SupportPage />} />
            <Route path="admin" element={<AdminPage />} />
            <Route path="contenido" element={<ContenidoPage />} />
            <Route path="interesados" element={<InteresadosPage />} />
          </Route>
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </Router>
      </DoctorRobotinProvider>
    </UserProvider>
  )
}

export default App
